/**
 * Browser-safe representation of the CampaignFilterInput accepted by the
 * Strata API. The editor only emits this shape after local validation.
 */
export type AudienceChannel = 'EMAIL' | 'INBOX' | 'PUSH' | 'SMS';

export type CampaignFilterOperator = 'EQ' | 'GTE' | 'IN' | 'LTE';
export type CampaignFilterService = 'ANY' | 'DATA' | 'SMS' | 'VOICE';
export type ByteUnit = 'B' | 'KB' | 'MB' | 'GB';

export interface CampaignFilterRule {
  field: CampaignFilterField;
  operator: CampaignFilterOperator;
  service?: CampaignFilterService;
  value?: boolean | number;
  values?: string[];
}

export interface CampaignFilterInput {
  version: 1;
  logic: 'AND';
  rules: CampaignFilterRule[];
}

export type FilterValueKind =
  | 'BOOLEAN'
  | 'ENUM'
  | 'NUMBER'
  | 'RECENCY'
  | 'STRING';
export type FilterFamily = 'Activity' | 'Usage' | 'Account' | 'App User';

export interface CampaignFilterFieldDefinition {
  family: FilterFamily;
  label: string;
  scope: 'ACCOUNT' | 'APP_USER';
  operators: readonly CampaignFilterOperator[];
  valueKind: FilterValueKind;
  values?: readonly string[];
  minimum?: number;
  maximum?: number;
  services?: readonly CampaignFilterService[];
  unit?: 'BYTES';
}

const ACTIVITY_TYPES = ['ANY', 'DATA', 'VOICE', 'SMS'] as const;
const LAST_ACTIVITY_TYPES = ['DATA', 'VOICE', 'SMS'] as const;
const NETWORK_PROVIDERS = [
  'MTN',
  'VODACOM',
  'CELL_C',
  'TELKOM',
  'RAIN',
  'MVNO',
  'OTHER',
] as const;
const TENURE_BANDS = [
  'UNKNOWN',
  '0_7D',
  '8_30D',
  '31_90D',
  '91_180D',
  '181_365D',
  '366D_PLUS',
] as const;
const AGE_BANDS = [
  'UNDER_18',
  '18_24',
  '25_34',
  '35_44',
  '45_54',
  '55_64',
  '65_PLUS',
  'UNKNOWN',
] as const;

/**
 * Mirror of the API's FILTER_FIELDS registry. Keep this declarative only: all
 * emitted data is still checked by the API contract, and the parity test keeps
 * this browser metadata synchronized with its authoritative constraints.
 */
export const campaignFilterFields = {
  activity_type: {
    family: 'Activity',
    label: 'Activity type',
    scope: 'ACCOUNT',
    operators: ['IN'],
    valueKind: 'ENUM',
    values: ACTIVITY_TYPES,
  },
  active_within_days: {
    family: 'Activity',
    label: 'Active within',
    scope: 'ACCOUNT',
    operators: ['EQ'],
    valueKind: 'RECENCY',
    services: ACTIVITY_TYPES,
    values: ['1', '7', '30', '60', '90', '180'],
  },
  inactive_for_days: {
    family: 'Activity',
    label: 'Inactive for',
    scope: 'ACCOUNT',
    operators: ['EQ'],
    valueKind: 'RECENCY',
    services: ACTIVITY_TYPES,
    values: ['1', '7', '30'],
  },
  data_bytes_1d: {
    family: 'Usage',
    label: 'Data used in the last 1 day',
    scope: 'ACCOUNT',
    operators: ['GTE', 'LTE'],
    valueKind: 'NUMBER',
    minimum: 0,
    maximum: Number.MAX_SAFE_INTEGER,
    unit: 'BYTES',
  },
  data_bytes_7d: {
    family: 'Usage',
    label: 'Data used in the last 7 days',
    scope: 'ACCOUNT',
    operators: ['GTE', 'LTE'],
    valueKind: 'NUMBER',
    minimum: 0,
    maximum: Number.MAX_SAFE_INTEGER,
    unit: 'BYTES',
  },
  data_bytes_30d: {
    family: 'Usage',
    label: 'Data used in the last 30 days',
    scope: 'ACCOUNT',
    operators: ['GTE', 'LTE'],
    valueKind: 'NUMBER',
    minimum: 0,
    maximum: Number.MAX_SAFE_INTEGER,
    unit: 'BYTES',
  },
  voice_calls_1d: {
    family: 'Usage',
    label: 'Voice calls in the last 1 day',
    scope: 'ACCOUNT',
    operators: ['GTE', 'LTE'],
    valueKind: 'NUMBER',
    minimum: 0,
    maximum: Number.MAX_SAFE_INTEGER,
  },
  voice_calls_7d: {
    family: 'Usage',
    label: 'Voice calls in the last 7 days',
    scope: 'ACCOUNT',
    operators: ['GTE', 'LTE'],
    valueKind: 'NUMBER',
    minimum: 0,
    maximum: Number.MAX_SAFE_INTEGER,
  },
  voice_calls_30d: {
    family: 'Usage',
    label: 'Voice calls in the last 30 days',
    scope: 'ACCOUNT',
    operators: ['GTE', 'LTE'],
    valueKind: 'NUMBER',
    minimum: 0,
    maximum: Number.MAX_SAFE_INTEGER,
  },
  sms_count_1d: {
    family: 'Usage',
    label: 'SMS sent in the last 1 day',
    scope: 'ACCOUNT',
    operators: ['GTE', 'LTE'],
    valueKind: 'NUMBER',
    minimum: 0,
    maximum: Number.MAX_SAFE_INTEGER,
  },
  sms_count_7d: {
    family: 'Usage',
    label: 'SMS sent in the last 7 days',
    scope: 'ACCOUNT',
    operators: ['GTE', 'LTE'],
    valueKind: 'NUMBER',
    minimum: 0,
    maximum: Number.MAX_SAFE_INTEGER,
  },
  sms_count_30d: {
    family: 'Usage',
    label: 'SMS sent in the last 30 days',
    scope: 'ACCOUNT',
    operators: ['GTE', 'LTE'],
    valueKind: 'NUMBER',
    minimum: 0,
    maximum: Number.MAX_SAFE_INTEGER,
  },
  active_days_30d: {
    family: 'Usage',
    label: 'Active days in the last 30 days',
    scope: 'ACCOUNT',
    operators: ['GTE', 'LTE'],
    valueKind: 'NUMBER',
    minimum: 0,
    maximum: 30,
  },
  last_activity_type: {
    family: 'Activity',
    label: 'Last activity type',
    scope: 'ACCOUNT',
    operators: ['IN'],
    valueKind: 'ENUM',
    values: LAST_ACTIVITY_TYPES,
  },
  has_ever_had_activity: {
    family: 'Activity',
    label: 'Has ever had activity',
    scope: 'ACCOUNT',
    operators: ['EQ'],
    valueKind: 'BOOLEAN',
  },
  has_linked_app_user: {
    family: 'Account',
    label: 'Has linked app user',
    scope: 'ACCOUNT',
    operators: ['EQ'],
    valueKind: 'BOOLEAN',
  },
  account_tenure_band: {
    family: 'Account',
    label: 'Account tenure',
    scope: 'ACCOUNT',
    operators: ['IN'],
    valueKind: 'ENUM',
    values: TENURE_BANDS,
  },
  attributed_partner_id: {
    family: 'Account',
    label: 'Attributed partner ID',
    scope: 'ACCOUNT',
    operators: ['IN'],
    valueKind: 'STRING',
  },
  activity_data_status: {
    family: 'Activity',
    label: 'Activity data status',
    scope: 'ACCOUNT',
    operators: ['IN'],
    valueKind: 'ENUM',
    values: ['AVAILABLE', 'UNAVAILABLE'],
  },
  age_band: {
    family: 'App User',
    label: 'Age band',
    scope: 'APP_USER',
    operators: ['IN'],
    valueKind: 'ENUM',
    values: AGE_BANDS,
  },
  language: {
    family: 'App User',
    label: 'Language',
    scope: 'APP_USER',
    operators: ['IN'],
    valueKind: 'STRING',
  },
  timezone: {
    family: 'App User',
    label: 'Timezone',
    scope: 'APP_USER',
    operators: ['IN'],
    valueKind: 'STRING',
  },
  current_network: {
    family: 'App User',
    label: 'Current network',
    scope: 'APP_USER',
    operators: ['IN'],
    valueKind: 'ENUM',
    values: NETWORK_PROVIDERS,
  },
  app_user_status: {
    family: 'App User',
    label: 'App user status',
    scope: 'APP_USER',
    operators: ['IN'],
    valueKind: 'ENUM',
    values: ['ACTIVE', 'INACTIVE', 'SUSPENDED'],
  },
  app_user_tenure_band: {
    family: 'App User',
    label: 'App-user tenure',
    scope: 'APP_USER',
    operators: ['IN'],
    valueKind: 'ENUM',
    values: TENURE_BANDS,
  },
  email_present: {
    family: 'App User',
    label: 'Email present',
    scope: 'APP_USER',
    operators: ['EQ'],
    valueKind: 'BOOLEAN',
  },
  email_verified: {
    family: 'App User',
    label: 'Email verified',
    scope: 'APP_USER',
    operators: ['EQ'],
    valueKind: 'BOOLEAN',
  },
} as const satisfies Record<string, CampaignFilterFieldDefinition>;

export type CampaignFilterField = keyof typeof campaignFilterFields;
export function campaignFilterDefinition(
  field: string,
): CampaignFilterFieldDefinition | undefined {
  if (!Object.prototype.hasOwnProperty.call(campaignFilterFields, field)) {
    return undefined;
  }
  return campaignFilterFields[field as CampaignFilterField];
}
export const byteUnits: readonly ByteUnit[] = ['B', 'KB', 'MB', 'GB'];
export const filterFamilies: readonly FilterFamily[] = [
  'Activity',
  'Usage',
  'Account',
  'App User',
];
